import React, { useContext, useMemo } from 'react';
import styles from './TextEditor.styles';
import { Button, Dropdown, Menu, Modal, Col, Row, Tag } from 'antd';
import { DownOutlined, EyeFilled } from '@ant-design/icons';
import LegalDocuementsTags from 'Components/Dashboard/LegalDocuments/LegalDocuementsTags';
import useTextEditorHook from './TextEditor.hook';
import JoditEditor from 'jodit-react';
import AppButton from 'Components/common/Forms/AppButton/AppButton';
import { AppContext } from 'App.context';
import { capitalize } from 'utils/TextUtils';
import AppTextInput from 'Components/common/AppTextInput/AppTextInput';
import SaveButton from 'Components/common/SaveButton/SaveButton';
import NewHotelSelector from 'Components/common/HotelSelector_new/NewHotelSelector';
import LanguageSelectorMultiple from 'Components/common/LanguageSelectorNew/LanguageSelectorMultiple';
import { useHotelLangSelection } from 'context/HotelLangSelectionContext';
import CustomButton from '../../Components/common/CustomButton/CustomButton';
import { copyTextToClipboard } from 'utils/TextUtils';

export default function TextEditor({
  type,
  isFirst,
  onSavePressed,
  onPreviewPressed,
  documentDefaultValue,
  labelDefaultValue,
  supportedTypes,
  disabled,
  selectedLanguage,
  isDefaultText,
  allLegalDocuments = [],
  allLegalDocumentLabels = [],
}) {
  const typeConfig = supportedTypes.find(legalType => legalType.id == type);
  const label = supportedTypes.find(legalType => legalType.id == `${type}-label`);

  const typeDefaultValue =
    typeConfig.defaultValue && typeof typeConfig.defaultValue == 'string'
      ? typeConfig.defaultValue
      : typeConfig.defaultValue
      ? typeConfig.defaultValue[selectedLanguage] || typeConfig.defaultValue['en']
      : null;

  const formatedDefaultValue =
    !documentDefaultValue && typeConfig.defaultValue ? typeDefaultValue : documentDefaultValue;

  const { state, actions } = useTextEditorHook(
    formatedDefaultValue,
    labelDefaultValue,
    isFirst,
    onPreviewPressed,
    selectedLanguage,
    isDefaultText
  );

  const { setSelectedHotels, onSave } = useHotelLangSelection();

  const {
    appActions: { translate },
    appState,
  } = useContext(AppContext);

  const joditEditorComponent = useMemo(
    () => (
      <JoditEditor
        ref={ref => actions.setTextAreaRef(ref)}
        editorRef={ref => actions.setEditorRef(ref)}
        style={{ flex: 1, height: '100%' }}
        value={state.editorValue || formatedDefaultValue}
        config={actions.getEditorConfig(disabled)}
      />
    ),
    [state.editorValue, formatedDefaultValue]
  );

  let tags = LegalDocuementsTags;
  tags = tagsOnlyForMeliaChain(tags);

  function tagsOnlyForMeliaChain(tags) {
    const tagsOnlyForMelia = [{ category: 'booking', tag: 'card_number_masked' }];

    const meliaTestHotels = [3918, 5212, 5453, 5915];

    const selectedHotelString = localStorage.getItem('selectedHotel');
    const selectedHotel = JSON.parse(selectedHotelString);

    if (!meliaTestHotels.includes(selectedHotel?.id)) {
      tagsOnlyForMelia.forEach(item => {
        const category = tags.find(c => c.name === item.category);
        if (category && Array.isArray(category.tags)) {
          category.tags = category.tags.filter(t => t !== item.tag);
        }
      });
    }

    return tags;
  }

  return (
    <div style={styles.editorContainer}>
      <Modal
        title={translate('legalDocuments.restoreDefault')}
        visible={state.showRecoverDefaultWarning}
        onOk={() => actions.onRecoverDefaultTextPressed(typeDefaultValue)}
        onCancel={() => actions.setShowRecoverDefaultWarning(false)}
      >
        <p>{translate('legalDocuments.restoreDefaultWarning')}</p>
      </Modal>

      <Modal
        title={translate('legalDocuments.deactivateTitle')}
        visible={state.showDeactivateWarning}
        onOk={() => {
          try {
            onSavePressed(type, '', '', false, false, null, 'all');
            actions.setLabelValue('');
            actions.forceEditorText('');
          } finally {
            actions.setShowDeactivateWarning(false);
          }
        }}
        onCancel={() => actions.setShowDeactivateWarning(false)}
        okText={translate('confirm')}
        cancelText={translate('cancel')}
      >
        <p>{translate('legalDocuments.deactivateWarning')}</p>
        <p>
          <b>{translate('legalDocuments.deactivateWarningAlert')}</b>
        </p>
      </Modal>

      <Row style={styles.headerContainer}>
        <Col style={styles.textContainer}>{capitalize(translate(`legalDocuments.${type}`))}</Col>
        <Col>
          {state.isTextActive ? (
            <Tag color="green">{translate('states.active')}</Tag>
          ) : (
            <Tag color="red">{translate('states.inactive')}</Tag>
          )}
        </Col>
      </Row>

      <div style={styles.header}>
        {label && (
          <AppTextInput
            key={type}
            name={`${translate('legalDocuments.labelHint').replace(
              '#',
              translate(`legalDocuments.${type}`)
            )}`}
            value={state.labelValue}
            onChange={text => actions.setLabelValue(text)}
          />
        )}

        {typeConfig.hasReplacementTags &&
          tags.map((tag, index) => {
            const menuOptions = tag?.tags
              ? tag.tags.map((tag, menuIndex) => {
                  return (
                    <Menu.Item key={menuIndex} onClick={() => actions.insertTag(tag)}>
                      {capitalize(translate(`legalDocuments.tags.${tag}`))}
                    </Menu.Item>
                  );
                })
              : null;

            return tag?.tags ? (
              <Dropdown key={index} overlay={<Menu>{menuOptions}</Menu>} arrow disabled={disabled}>
                <Button size={'small'}>
                  {capitalize(translate(`legalDocuments.tags.${tag.name}`))} <DownOutlined />
                </Button>
              </Dropdown>
            ) : (
              <Button
                key={index}
                size={'small'}
                style={{ margin: 4 }}
                onClick={() => actions.insertTag(tag)}
              >
                {capitalize(translate(`legalDocuments.tags.${tag}`))}
              </Button>
            );
          })}
      </div>

      <div>{joditEditorComponent}</div>

      <div style={styles.buttonContainer}>
        {typeConfig.defaultValue && !disabled && (
          <AppButton
            secondary
            action={() => actions.setShowRecoverDefaultWarning(true)}
            text={translate('legalDocuments.restoreDefault')}
          />
        )}
        <AppButton
          secondary
          action={() => onPreviewPressed(actions.getText())}
          text={translate('legalDocuments.previewBtn')}
          icon={<EyeFilled style={{ color: '#708393' }} />}
        />
      </div>

      <div style={styles.saveButtonContainer}>
        {!disabled && (
          <Row gutter={16} align="middle">
            <Col>
              <SaveButton
                action={applyToAllHotels =>
                  onSavePressed(type, actions.getText(), state.labelValue, applyToAllHotels)
                }
                text={translate('save')}
              />
            </Col>
            <Col>
              {state.isTextActive && (
                <CustomButton
                  style={styles.deactivateButton}
                  text={translate('legalDocuments.deactivateButton')}
                  onClick={() => {
                    copyTextToClipboard(actions.getText());
                    actions.setShowDeactivateWarning(true);
                  }}
                />
              )}
            </Col>
          </Row>
        )}
      </div>

      {(appState?.email.includes('@civitfun') || appState?.superUser) && (
        <>
          <hr />

          <div style={styles.duplicateLegalTextContainer}>
            <div>
              <div style={styles.textContainer}>{`${translate(
                'legalDocuments.header_text'
              )} ${type}`}</div>
            </div>

            <NewHotelSelector
              enableAllHotels={true}
              mode="multiple"
              onHotelSelected={hotels => {
                setSelectedHotels(hotels);
              }}
              autoSelect={true}
            />

            <LanguageSelectorMultiple isLoading={false} />

            <AppButton
              text={translate('save')}
              action={() => onSave(type, allLegalDocuments, allLegalDocumentLabels, onSavePressed)}
            ></AppButton>
          </div>
        </>
      )}
    </div>
  );
}
